import React, { createContext, useContext, useEffect } from 'react';

/* ========================================================================= */
/* Context(s) */
/* ========================================================================= */

/** @type {import('$types/components/contexts').AnalyticsContext} */
const AnalyticsContext = createContext(null);

/* ========================================================================= */
/* Hook(s) */
/* ========================================================================= */

const useAnalytics = () => {
    const context = useContext(AnalyticsContext);

    if (context === undefined) {
        throw new Error('useAnalytics must be used within a AnalyticsProvider.');
    }

    return context;
};

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/** @type {import('$types/components/contexts').AnalyticsContextProvider} */
const AnalyticsProvider = ({ children }) => {
    useEffect(() => {
        const url = new URL(window.location.href);
        const currentPageUrl = url.href;

        const utmParams = {
            utm_source: url.searchParams.get('utm_source'),
            utm_medium: url.searchParams.get('utm_medium'),
            utm_campaign: url.searchParams.get('utm_campaign'),
            utm_content: url.searchParams.get('utm_content'),
            utm_term: url.searchParams.get('utm_term')
        };

        // Filter out null values and store in sessionStorage
        const hasUTMParams = Object.values(utmParams).some(value => value !== null);
        if (hasUTMParams) {
            Object.entries(utmParams).forEach(([key, value]) => {
                if (value !== null) {
                    window.sessionStorage.setItem(key, value);
                }
            });
        }

        // Store first landing page in sessionStorage (only if not already set)
        const firstLandingPage = window.sessionStorage.getItem('first_landing_page');
        if (!firstLandingPage) {
            window.sessionStorage.setItem('first_landing_page', currentPageUrl);
        }

        // Store current landing page in sessionStorage
        window.sessionStorage.setItem('landing_page', currentPageUrl);

        // Backward compatibility: keep the old 'utm' parameter if it exists
        if (url.searchParams.has('utm')) {
            window.sessionStorage.setItem('utm', url.searchParams.get('utm'));
        }

    }, []);

    const getTrackingData = () => {
        if (typeof window === 'undefined') {
            return {
                utm_source: null,
                utm_medium: null,
                utm_campaign: null,
                utm_content: null,
                utm_term: null,
                landing_page: null,
                first_landing_page: null
            };
        }

        const rawData = {
            utm_source: window.sessionStorage.getItem('utm_source'),
            utm_medium: window.sessionStorage.getItem('utm_medium'),
            utm_campaign: window.sessionStorage.getItem('utm_campaign'),
            utm_content: window.sessionStorage.getItem('utm_content'),
            utm_term: window.sessionStorage.getItem('utm_term'),
            landing_page: window.sessionStorage.getItem('landing_page'),
            first_landing_page: window.sessionStorage.getItem('first_landing_page')
        };

        // Filter out null, undefined, empty, and literal "null"/"undefined" strings
        const isValid = (value) => value && value !== 'null' && value !== 'undefined' && value.trim() !== '';

        const cleanedData = {
            utm_source: isValid(rawData.utm_source) ? rawData.utm_source : null,
            utm_medium: isValid(rawData.utm_medium) ? rawData.utm_medium : null,
            utm_campaign: isValid(rawData.utm_campaign) ? rawData.utm_campaign : null,
            utm_content: isValid(rawData.utm_content) ? rawData.utm_content : null,
            utm_term: isValid(rawData.utm_term) ? rawData.utm_term : null,
            landing_page: isValid(rawData.landing_page) ? rawData.landing_page : null,
            first_landing_page: isValid(rawData.first_landing_page) ? rawData.first_landing_page : null
        };

        return cleanedData;
    };

    const getUTM = () => {
        if (typeof window === 'undefined') {
            return null;
        }
        return window.sessionStorage.getItem('utm');
    };

    const createSourceSummary = (existingTrackingData) => {

        const trackingData = existingTrackingData || getTrackingData();
        const sourceParts = [];

        // Use sensible defaults for source and medium if missing
        const source = trackingData.utm_source || 'direct';
        const medium = trackingData.utm_medium || 'none';

        sourceParts.push(`Source: ${source}`);
        sourceParts.push(`Medium: ${medium}`);

        // Only include optional fields if they have valid values
        if (trackingData.utm_campaign) {
            sourceParts.push(`Campaign: ${trackingData.utm_campaign}`);
        }
        if (trackingData.utm_content) {
            sourceParts.push(`Content: ${trackingData.utm_content}`);
        }
        if (trackingData.utm_term) {
            sourceParts.push(`Term: ${trackingData.utm_term}`);
        }
        if (trackingData.landing_page) {
            sourceParts.push(`Landing Page: ${trackingData.landing_page}`);
        }
        if (trackingData.first_landing_page && trackingData.first_landing_page !== trackingData.landing_page) {
            sourceParts.push(`First Landing: ${trackingData.first_landing_page}`);
        }

        const sourceSummary = `${sourceParts.join(' | ')}`;
        return sourceSummary;
    };

    const value = { getUTM, getTrackingData, createSourceSummary };

    return (
        <AnalyticsContext.Provider value={value}>
            {children}
        </AnalyticsContext.Provider>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

AnalyticsContext.displayName = 'AnalyticsContext';

export {
    AnalyticsProvider,
    useAnalytics
};
